import React, { useState, useEffect } from 'react';

/**
 * Internal tool for account managers: builds a link that pre-fills the form with a configured
 * quote. Hidden behind an arrow-key sequence or ?share=true.
 *
 * The URL is now built by buildShareParams in checkout/state/urlPrefill.js and passed in as one
 * prop. This component previously received a 12-field formState object and reassembled the URL
 * itself -- a second, independent encoder that had already drifted from the parser: it keyed on
 * plan.name (so renaming a plan broke every link already sent) and omitted questionAnswers
 * entirely (so a shared quote silently dropped every priced upsell).
 */
export default function ShareConfigButton({ shareUrl }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);

  // Check for share=true query parameter on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shareParam = urlParams.get('share');
    if (shareParam === 'true') {
      setIsUnlocked(true);
    }
  }, []);

  // Arrow key sequence detection
  useEffect(() => {
    let keySequence = [];
    
    const handleKeyDown = (event) => {
      // Only listen for arrow keys
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)) {
        return;
      }

      keySequence.push(event.code);
      // Keep only the last 4 keys
      keySequence = keySequence.slice(-4);
      
      // Check if sequence matches: Up, Down, Left, Right
      const expectedSequence = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (keySequence.length === 4 && 
          keySequence.every((key, index) => key === expectedSequence[index])) {
        setIsUnlocked(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        alert('Failed to copy URL. Please copy manually.');
      }
      document.body.removeChild(textArea);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCopySuccess(false);
  };

  return (
    <>
      {/* Sticky Share Button - Only show when unlocked */}
      {isUnlocked && (
        <div className="fixed right-6 bottom-6 z-40">
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="group relative inline-flex items-center justify-center w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg hover:shadow-xl hover:bg-primary-700 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-primary-300 focus:ring-offset-2 transform hover:scale-110 active:scale-95"
          title="Share Configuration"
        >
          {/* Share Icon */}
          <svg className="w-6 h-6 transition-transform duration-300 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
          </svg>
          
          {/* Tooltip */}
          <div className="absolute right-full mr-3 px-3 py-2 bg-gray-900 text-white text-base rounded-sm shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
            Share Configuration
            <div className="absolute left-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-l-4 border-l-gray-900 border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
          </div>
        </button>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={closeModal}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-sm text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Share Your Configuration
                    </h3>
                    <div className="mt-2">
                      <p className="text-base text-gray-500">
                        Copy this URL to share the current product selections with others. 
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <div className="flex flex-col gap-3">
                    <div>
                      <label htmlFor="share-url" className="block text-base font-medium text-gray-700 mb-1">
                        Shareable URL:
                      </label>
                      <div className="flex gap-2">
                        <input
                          id="share-url"
                          type="text"
                          value={shareUrl}
                          readOnly
                          className="flex-1 rounded-sm border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base px-3 py-2 bg-gray-50"
                        />
                        <button
                          type="button"
                          onClick={copyToClipboard}
                          className={`px-4 py-2 text-base font-medium rounded-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                            copySuccess 
                              ? 'bg-green-600 text-white hover:bg-success-700' 
                              : 'bg-primary-600 text-white hover:bg-primary-700'
                          }`}
                        >
                          {copySuccess ? (
                            <>
                              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Copied!
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-base text-blue-700">
                            When someone visits this URL, they'll see the exact product selections pre-filled in the form. 
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-full inline-flex justify-center rounded-sm border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
